import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Card, Button, useTheme, ProgressBar } from 'react-native-paper';
import {
  Trophy,
  TrendingUp,
  Target,
  Sparkles,
  ChevronRight,
  Award,
  Star,
} from 'lucide-react-native';
import type {
  DashboardInsights,
  ProgressInsight,
  Milestone,
} from '@coffeeplexity/shared';

interface ProgressCelebrationProps {
  insights: DashboardInsights;
  onViewProgress: () => void;
  onViewAchievements: () => void;
}

export function ProgressCelebration({
  insights,
  onViewProgress,
  onViewAchievements,
}: ProgressCelebrationProps) {
  const theme = useTheme();

  // Find the most significant achievement or improvement to celebrate
  const celebrationData = useMemo(() => {
    const { progressInsights, upcomingMilestones, streakInfo } = insights;

    // Check for recent achievements
    const recentAchievements = progressInsights.filter(
      insight => insight.type === 'achievement' && insight.aiGenerated
    );

    if (recentAchievements.length > 0) {
      const achievement = recentAchievements[0];
      return {
        type: 'achievement',
        title: achievement.title,
        description: achievement.description,
        icon: Trophy,
        color: '#FFD700', // Gold
        actionText: 'View Achievements',
        onAction: onViewAchievements,
      };
    }

    // Check for significant improvements
    const improvements = progressInsights.filter(
      insight => insight.type === 'improvement' && insight.change > 0.1
    );

    if (improvements.length > 0) {
      const improvement = improvements[0];
      return {
        type: 'improvement',
        title: improvement.title,
        description: improvement.description,
        icon: TrendingUp,
        color: '#4ECDC4', // Teal
        actionText: 'View Progress',
        onAction: onViewProgress,
      };
    }

    // Check for milestone progress
    if (upcomingMilestones.length > 0) {
      const milestone = upcomingMilestones[0];
      const progressPercent = (milestone.progress / milestone.target) * 100;

      if (progressPercent >= 75) {
        return {
          type: 'milestone',
          title: `Almost there! ${Math.round(progressPercent)}% complete`,
          description: milestone.title,
          icon: Target,
          color: '#FF6B35', // Orange
          actionText: 'View Progress',
          onAction: onViewProgress,
          milestone,
        };
      }
    }

    // Check for streak milestones
    if (streakInfo.current > 0) {
      const isStreakMilestone =
        streakInfo.current % 7 === 0 || streakInfo.current % 30 === 0;

      if (isStreakMilestone) {
        return {
          type: 'streak',
          title: `${streakInfo.current} Day Streak! 🔥`,
          description: 'Your consistency is paying off!',
          icon: Award,
          color: '#FF6B35', // Orange
          actionText: 'Keep Going',
          onAction: onViewProgress,
        };
      }
    }

    return null;
  }, [insights, onViewProgress, onViewAchievements]);

  // If no celebration data, don't render
  if (!celebrationData) {
    return null;
  }

  const {
    type,
    title,
    description,
    icon: Icon,
    color,
    actionText,
    onAction,
    milestone,
  } = celebrationData;

  return (
    <Card style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <Card.Content style={styles.content}>
        {/* Celebration Header */}
        <View style={styles.header}>
          <View
            style={[styles.iconContainer, { backgroundColor: `${color}20` }]}
          >
            <Icon size={24} color={color} />
            <Sparkles size={16} color={color} style={styles.sparkleIcon} />
          </View>

          <View style={styles.headerText}>
            <Text
              variant='labelMedium'
              style={[styles.celebrationType, { color }]}
            >
              {type.toUpperCase()}
            </Text>
            <Text
              variant='titleMedium'
              style={[
                styles.celebrationTitle,
                { color: theme.colors.onSurface },
              ]}
            >
              {title}
            </Text>
          </View>
        </View>

        {/* Description */}
        <Text
          variant='bodyMedium'
          style={[styles.description, { color: theme.colors.onSurfaceVariant }]}
        >
          {description}
        </Text>

        {/* Milestone Progress Bar (if applicable) */}
        {milestone && (
          <View style={styles.progressContainer}>
            <View style={styles.progressHeader}>
              <Text
                variant='bodySmall'
                style={[
                  styles.progressLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Progress to milestone
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.progressValue,
                  { color: theme.colors.onSurface },
                ]}
              >
                {milestone.progress} / {milestone.target}
              </Text>
            </View>

            <ProgressBar
              progress={milestone.progress / milestone.target}
              color={color}
              style={styles.progressBar}
            />
          </View>
        )}

        {/* Recent Progress Insights */}
        {insights.progressInsights.length > 0 && (
          <View style={styles.insightsContainer}>
            <Text
              variant='labelMedium'
              style={[styles.insightsTitle, { color: theme.colors.onSurface }]}
            >
              Recent Progress
            </Text>

            {insights.progressInsights.slice(0, 2).map((insight, index) => (
              <View key={insight.id} style={styles.insightItem}>
                <View style={styles.insightIcon}>
                  {insight.type === 'achievement' && (
                    <Trophy size={14} color={color} />
                  )}
                  {insight.type === 'improvement' && (
                    <TrendingUp size={14} color={color} />
                  )}
                  {insight.type === 'milestone' && (
                    <Target size={14} color={color} />
                  )}
                  {insight.type === 'challenge' && (
                    <Star size={14} color={color} />
                  )}
                </View>

                <View style={styles.insightContent}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.insightTitle,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {insight.title}
                  </Text>

                  {insight.change !== 0 && (
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.insightChange,
                        {
                          color:
                            insight.change > 0
                              ? '#4ECDC4'
                              : theme.colors.onSurfaceVariant,
                        },
                      ]}
                    >
                      {insight.change > 0 ? '+' : ''}
                      {Math.round(insight.change * 100)}%
                    </Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Action Button */}
        <Button
          mode='contained'
          onPress={onAction}
          style={[styles.actionButton, { backgroundColor: color }]}
          contentStyle={styles.actionButtonContent}
          labelStyle={styles.actionButtonLabel}
          icon={({ size }) => <ChevronRight size={size} color='white' />}
        >
          {actionText}
        </Button>
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  content: {
    paddingVertical: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    position: 'relative',
  },
  sparkleIcon: {
    position: 'absolute',
    top: -4,
    right: -4,
  },
  headerText: {
    flex: 1,
  },
  celebrationType: {
    fontWeight: '600',
    fontSize: 11,
    marginBottom: 2,
  },
  celebrationTitle: {
    fontWeight: '700',
  },
  description: {
    lineHeight: 20,
    marginBottom: 16,
  },
  progressContainer: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 12,
  },
  progressValue: {
    fontSize: 12,
    fontWeight: '600',
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  insightsContainer: {
    marginBottom: 20,
  },
  insightsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  insightIcon: {
    width: 24,
    alignItems: 'center',
    marginRight: 8,
  },
  insightContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  insightTitle: {
    flex: 1,
    fontSize: 12,
  },
  insightChange: {
    fontSize: 12,
    fontWeight: '600',
  },
  actionButton: {
    borderRadius: 8,
    minHeight: 44,
  },
  actionButtonContent: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    flexDirection: 'row-reverse',
  },
  actionButtonLabel: {
    fontWeight: '600',
    fontSize: 16,
    color: 'white',
    lineHeight: 20,
    textAlign: 'center',
  },
});
